import BaseMapPopup from 'components/BaseMap/components/BaseMapPopup';
import NewStatusBadge from 'components/NodeStatus/NewStatusBadge';
import { isBuoyDevice } from 'features/Integrations/features/ConnectedDevices/features/Buoy/utils/buoyDeviceUtils';
import { useDeviceStatusLoading } from 'features/Integrations/hooks/useDeviceStatusLoading';
import { ResolveDeviceType } from 'features/Integrations/utils/deviceComparisionUtils';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { getDeviceStatusCode } from 'utils/devices/icon.utils';
import '../../assets/deviceMap.css';
import DeviceMapPopupContent from './DeviceMapPopupContent';

function getDeviceTitle(device) {
  if (isBuoyDevice(device)) return device?.alias || device?.name || 'Buoy';
  if (ResolveDeviceType(device) === 'Monitoring Station') {
    return device?.alias && String(device.alias).trim() !== ''
      ? device.alias
      : device?.monitoring_location_name || device?.name || 'Monitoring Station';
  }
  return `${device?.device_type !== 'Manual Road Closure' && device?.static_id ? `${device.static_id} ` : ''}${
    device?.alias || device?.name || 'Device'
  }`;
}

function getDeviceSubtitle(device) {
  const type = isBuoyDevice(device) ? 'Buoy' : ResolveDeviceType(device).replace(/_/g, ' ');
  const id = device?.static_id || device?.monitoring_location_id || device?.node_id;
  return [type, id].filter(Boolean).join(' - ');
}

function getDeviceTimestamp(device) {
  const raw = device?.data?.[0]?.ts || device?.data?.[0]?.date_created;
  if (!raw) return null;
  const parsed =
    typeof raw === 'number' || /^\d+$/.test(String(raw)) ? new Date(Number(raw)) : new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return `Updated ${parsed.toLocaleString()}`;
}

// The stale rule lives in `getDeviceStatusCode` (utils/devices/icon.utils.js), so a
// telemetry device whose newest row has aged past `DEVICE_DATA_STALE_MS` already reads
// Offline here; event-only types (an idle siren) are exempt there. See
// Fusion-Backend/Docs/issues/planning/device-data-on-demand/CONTRACT.md.
function getDeviceStatus(device) {
  const status = getDeviceStatusCode(device);
  if (!device?.data && device?.device_type !== 'Overwatch VMS') return 'Offline';
  return status || 'Offline';
}

function hasExpandableDetails(device) {
  if (isBuoyDevice(device)) return true;
  return ['Stage', 'Monitoring Station'].includes(ResolveDeviceType(device));
}

const DeviceMapPopup = ({ device, onClose, onViewDevice, viewLabel = 'View device' }) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  // No point yet and the covering fetch still in flight: 'Loading', not a false Offline.
  const isLoading = useDeviceStatusLoading(device);

  if (!device) return null;
  const showDetailsToggle = hasExpandableDetails(device);

  return (
    <BaseMapPopup
      className="connected-device-map-popup"
      title={getDeviceTitle(device)}
      subtitle={getDeviceSubtitle(device)}
      eyebrow={device?.system_type || 'Connected Device'}
      status={<NewStatusBadge status={isLoading ? 'Loading' : getDeviceStatus(device)} />}
      statusClassName="connected-device-map-popup__status"
      meta={getDeviceTimestamp(device)}
      onClose={onClose}
      density="compact"
      footer={
        (showDetailsToggle || onViewDevice) && (
          <>
            {showDetailsToggle && (
              <Button
                variant="link"
                size="sm"
                className="connected-device-map-popup__details-link text-primary text-decoration-none px-1 py-0 fw-semibold"
                onClick={() => setDetailsExpanded((prev) => !prev)}
                aria-expanded={detailsExpanded}
              >
                {detailsExpanded ? 'Hide' : 'More Details'}
              </Button>
            )}
            {onViewDevice && (
              <Button
                variant="primary"
                size="sm"
                className="connected-device-map-popup__view-button px-3"
                onClick={onViewDevice}
              >
                {viewLabel}
              </Button>
            )}
          </>
        )
      }
    >
      <DeviceMapPopupContent device={device} detailsExpanded={detailsExpanded} />
    </BaseMapPopup>
  );
};

export default DeviceMapPopup;
